import { useId, useState } from 'react'
import {
  ActionButton,
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  IDetailsRowProps,
  SelectionMode,
  Stack,
  Text,
} from '@fluentui/react'
import { QuarterResult } from '../../api/models'
import Disclosure from './Disclosure'
import ExcludedUsersNote from './ExcludedUsersNote'
import KrStatusBadge from './KrStatusBadge'
import SubscribersTable from './SubscribersTable'
import { formatQuarterDates, krStatusOf } from './krFormat'
import { MUTED_TEXT } from './krStyles'

type PastQuartersTableProps = {
  history: QuarterResult[]
}

type RowToggle = {
  isExpanded: (quarter: QuarterResult) => boolean
  toggle: (quarter: QuarterResult) => void
  regionIdFor: (quarter: QuarterResult) => string
}

const quarterKey = (quarter: QuarterResult): string => `${quarter.year}-${quarter.quarter}`

const buildColumns = ({ isExpanded, toggle, regionIdFor }: RowToggle): IColumn[] => [
  { key: 'label', name: 'Quarter', fieldName: 'label', minWidth: 70, maxWidth: 100 },
  {
    key: 'dates',
    name: 'Dates',
    minWidth: 150,
    maxWidth: 220,
    onRender: (quarter: QuarterResult) => formatQuarterDates(quarter),
  },
  {
    key: 'subscribers',
    name: 'Subscribers',
    minWidth: 80,
    maxWidth: 110,
    onRender: (quarter: QuarterResult) => quarter.subscriber_count.toLocaleString(),
  },
  {
    key: 'result',
    name: 'Result',
    minWidth: 100,
    maxWidth: 130,
    onRender: (quarter: QuarterResult) => <KrStatusBadge status={krStatusOf(quarter)} />,
  },
  {
    key: 'view',
    name: 'Details',
    minWidth: 120,
    onRender: (quarter: QuarterResult) => (
      <ActionButton
        iconProps={{ iconName: isExpanded(quarter) ? 'ChevronDown' : 'ChevronRight' }}
        aria-expanded={isExpanded(quarter)}
        aria-controls={regionIdFor(quarter)}
        aria-label={`${isExpanded(quarter) ? 'Hide' : 'View'} subscribers for ${quarter.label}`}
        onClick={() => toggle(quarter)}
        styles={{ root: { height: 24, paddingLeft: 0 } }}
      >
        {isExpanded(quarter) ? 'Hide' : 'View'}
      </ActionButton>
    ),
  },
]

const PastQuartersTable = ({ history }: PastQuartersTableProps) => {
  const [expandedKeys, setExpandedKeys] = useState<ReadonlySet<string>>(new Set())
  const idPrefix = useId()

  const rowToggle: RowToggle = {
    isExpanded: (quarter) => expandedKeys.has(quarterKey(quarter)),
    toggle: (quarter) =>
      setExpandedKeys((current) => {
        const next = new Set(current)
        const key = quarterKey(quarter)
        if (next.has(key)) next.delete(key)
        else next.add(key)
        return next
      }),
    regionIdFor: (quarter) => `${idPrefix}-${quarterKey(quarter)}`,
  }

  const renderRow = (props?: IDetailsRowProps, defaultRender?: (props?: IDetailsRowProps) => JSX.Element | null) => {
    if (!props || !defaultRender) return null
    const quarter = props.item as QuarterResult
    const isExpanded = rowToggle.isExpanded(quarter)
    return (
      <div>
        {defaultRender(props)}
        <div id={rowToggle.regionIdFor(quarter)} hidden={!isExpanded} style={{ padding: '4px 12px 12px 24px' }}>
          {isExpanded && (
            <Stack tokens={{ childrenGap: 4 }}>
              <SubscribersTable
                subscribers={quarter.subscribers}
                ariaLabel={`Subscribers in ${quarter.label}`}
                emptyMessage="No subscribers were counted this quarter."
              />
              <ExcludedUsersNote quarter={quarter} />
            </Stack>
          )}
        </div>
      </div>
    )
  }

  return (
    <Disclosure label="Past quarters">
      {history.length > 0 ? (
        <DetailsList
          items={history}
          columns={buildColumns(rowToggle)}
          getKey={quarterKey}
          selectionMode={SelectionMode.none}
          layoutMode={DetailsListLayoutMode.justified}
          compact
          ariaLabelForGrid="Past quarters"
          onRenderRow={renderRow}
          onShouldVirtualize={() => false}
        />
      ) : (
        <Text styles={{ root: { ...MUTED_TEXT.root, padding: '8px 0', display: 'block' } }}>No earlier quarters yet.</Text>
      )}
    </Disclosure>
  )
}

export default PastQuartersTable
